import React, { useEffect, useState } from "react";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import LightModeIcon from "@mui/icons-material/LightMode";
import NextLink from "next/link";
import { useRouter } from "next/router";
import SideMenu from "../SideMenu";
import { menuItem } from "./menuItem";
import generateConfig from "../../../util/config";
import { SelectThemeType } from "../../../types/types";
import useAuthProvider from "../../../store/AuthProvider";
import useMenuAnimation from "../../../hooks/useMenuAnimation";

const config = generateConfig();

export interface HeaderProps {
  toggleTheme?: React.MouseEventHandler<HTMLButtonElement>;
  selectedTheme: SelectThemeType;
}

export default function Header({ toggleTheme, selectedTheme }: HeaderProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const isLightMode = selectedTheme.type === "light";

  const scope = useMenuAnimation(isOpen);

  const auth = useAuthProvider();
  const router = useRouter();

  const ignoredRoutes =
    router.pathname === "/signup" ||
    router.pathname === "/signin" ||
    router.pathname === "/login";

  useEffect(() => {
    if (!ignoredRoutes) {
      sessionStorage && sessionStorage.setItem("prevPath", router.asPath);
    }
  }, [router.asPath, ignoredRoutes]);

  const filteredMenu = menuItem.filter((item) => {
    if (auth.isAuthenticated) {
      return !item.hideOnAuthenticate || item.isResctricted;
    }
    return !item.isResctricted;
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ minHeight: 76, width: "100%" }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <MuiLink
          component={NextLink}
          href="/"
          underline="none"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          <Typography variant="h2" sx={{ fontWeight: 700 }}>
            {config.logoName || "Krushna"}
          </Typography>
        </MuiLink>
        <Typography
          variant="body2"
          sx={{
            display: { xs: "none", md: "block" },
            color: "text.secondary",
            maxWidth: 220,
          }}
        >
          Experience design engineer crafting thoughtful interfaces.
        </Typography>
      </Stack>

      {matches ? (
        <Stack direction="row" spacing={3} alignItems="center">
          {filteredMenu.map((item) => {
            const isActive = item.path === router.pathname;
            return (
              <MuiLink
                key={item.path}
                component={NextLink}
                href={item.path}
                underline="none"
                sx={{
                  position: "relative",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  transition: "color 0.2s ease",
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {item.label}
                {isActive ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      bottom: -8,
                      width: "100%",
                      height: 3,
                      borderRadius: 999,
                      background: theme.palette.primary.main,
                    }}
                  />
                ) : null}
              </MuiLink>
            );
          })}
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconButton onClick={toggleTheme} aria-label="Toggle theme" size="large">
          {isLightMode ? <Brightness4Icon /> : <LightModeIcon />}
        </IconButton>
        {auth.isAuthenticated && matches ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              src={auth.user?.profile || undefined}
              alt={auth.user?.name || "User avatar"}
              sx={{ width: 32, height: 32 }}
            />
            <Typography variant="body2" fontWeight={600} noWrap>
              {auth.user?.name || ""}
            </Typography>
          </Stack>
        ) : null}
        {matches ? (
          <Button
            component={NextLink}
            href="/contact"
            variant="contained"
            sx={{ borderRadius: "999px", fontWeight: 600 }}
          >
            Work with me
          </Button>
        ) : null}
        {!matches ? (
          <IconButton
            aria-label={isOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setIsOpen((prev) => !prev)}
            size="large"
          >
            {isOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
          </IconButton>
        ) : null}
      </Stack>

      <Box
        ref={scope}
        sx={{
          zIndex: 1000,
        }}
      >
        <Backdrop
          open={isOpen}
          onClick={() => {
            setIsOpen(false);
          }}
          sx={{ backgroundColor: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(6px)" }}
        >
          <SideMenu setIsOpen={setIsOpen} isMobile={!matches} />
        </Backdrop>
      </Box>
    </Stack>
  );
}
